import { useQuery } from '@tanstack/react-query';
import { axiosGet } from '../api/axios';
import { deriveContentReviewTypes } from '../containers/peer-listing/utils/contentReview';
import { ListenerAudio } from './useListenerAudio';

export interface ListenerRoleResponse {
  count: number;
  data: ListenerRole[];
}

export interface ListenerRole {
  about_me: string;
  about_me_rejected: boolean;
  age_group: string;
  background_tags: BackgroundTag[];
  calls_succeeded: number;
  connection_requests: number;
  group_name: string | null;
  can_take_calls: boolean;
  connection_percent: number;
  credentials_region: string;
  credentials_type: any;
  credentials_value: string;
  current_profile_photo: ProfilePhoto;
  proposed_profile_photo?: ProfilePhoto;
  earnings: number;
  earnings_last_week?: number;
  experiences?: ListenerAudio[];
  num_experiences: number;
  num_playbacks: number;
  num_followers: number;
  id: number;
  impact_score: number;
  is_active: boolean;
  is_peer_active: boolean;
  listener_grade: string;
  listener_tags: [string, number][];
  notes: string;
  onboarding_completed: string;
  premier_listener: boolean;
  profile_photo_approved: boolean;
  profile_photo_file_url: string;
  profile_photo_original_file_path: string;
  profile_photo_process_configuration: ProfilePhotoProcessConfiguration;
  proposed_about_me: string;
  state: ListenerState;
  tier_advancement: number;
  tier_id: number;
  has_required_experiences?: boolean;
  content_optional?: boolean;
  content_optional_from_tier?: boolean;
  content_optional_from_client?: boolean;
  user: User;
  verification_type: any;
  verified_listener: boolean;
}

export type ListenerState = 'onboarding_peer' | 'rejected_peer' | 'active_peer' | 'upgrading_peer' | 'active_listener';
export const PeerType = {
  ONBOARDING_PEER: 'onboarding_peer',
  REJECTED_PEER: 'rejected_peer',
  ACTIVE_PEER: 'active_peer',
  UPGRADING_PEER: 'upgrading_peer',
  ACTIVE_LISTENER: 'active_listener',
};

export type TagStatus = 'APPROVED' | 'REJECTED' | 'UNGRADED';

export interface BackgroundTag {
  id: number;
  tag_id: number;
  created_at: Date;
  status: TagStatus;
  media: Media[];
  tag: Tag;
}

export interface Media {
  file_key: string;
  file_url: string;
  file_name?: string;
}

export interface Tag {
  id: number;
  name: string;
}

export interface ProfilePhoto {
  id: number;
  status: string;
  file_url: string;
  original_file_path: string;
  process_configuration: ProfilePhotoProcessConfiguration;
  square_url: string;
}

export interface ProfilePhotoProcessConfiguration {
  offset: any;
  rotation: any;
}

export interface User {
  created_at: string;
  date_of_birth: string;
  display_name: string;
  display_name_rejected: boolean;
  email_address: string;
  first_name: string;
  id: number;
  last_name: string;
  proposed_display_name: string;
  test_user: boolean;
}

export interface PeerOptions {
  aboutMeStatus?: string;
  activePeers?: boolean;
  ageGroup?: string[];
  backgroundTagApproved?: boolean;
  contentTagIds?: number[];
  displayNameStatus?: string;
  ethnicityTags?: number[];
  familyTags?: number[];
  genderTags?: number[];
  groupName?: string;
  hasBackgroundTags?: boolean;
  hideIncompletePeers?: boolean;
  isPeerActive?: string[];
  testUsers?: string[];
  languageTags?: number[];
  limit?: number;
  orderBy?: string;
  page?: number;
  profilePhotoApproved?: boolean;
  relationshipTags?: number[];
  search?: string;
  spiritualityTags?: number[];
  tierId?: number[];
  userType?: string[];
  clientIds?: number[];
  domainId?: number;
  contentCreatedAfter?: string;
  contentCreatedBefore?: string;
  gradedByUserIds?: number[];
  gradedAfter?: string;
  gradedBefore?: string;
  needsExperienceApproval?: boolean;
  needsExperienceApprovalMediaTypes?: Array<'audio' | 'video'>;
  needsAnyContentReview?: boolean;
  tagIds?: number[];
}

const buildQueryParams = (peerOptions: PeerOptions): any => {
  let params: any = {};

  const contentReviewTypes = deriveContentReviewTypes(peerOptions);
  // Only include keys the listeners API supports.
  const mapOptionToParam = {
    age_group: peerOptions.ageGroup?.length ? peerOptions.ageGroup.join(',') : undefined,
    active_peers:
      peerOptions.isPeerActive?.length === 1 &&
      (peerOptions.isPeerActive[0] === 'true' || peerOptions.isPeerActive[0] === 'false')
        ? peerOptions.isPeerActive[0]
        : undefined,
    ethnicity_tags: peerOptions.ethnicityTags?.length ? peerOptions.ethnicityTags.join(',') : undefined,
    family_tags: peerOptions.familyTags?.length ? peerOptions.familyTags.join(',') : undefined,
    gender_tags: peerOptions.genderTags?.length ? peerOptions.genderTags.join(',') : undefined,
    topic_tag_ids: peerOptions.contentTagIds?.length ? peerOptions.contentTagIds.join(',') : undefined,
    group_name: peerOptions.groupName || undefined,
    has_background_tags: peerOptions.hasBackgroundTags !== undefined ? peerOptions.hasBackgroundTags : undefined,
    hide_incomplete_peers:
      peerOptions.hideIncompletePeers === true ? true : peerOptions.hideIncompletePeers === false ? false : undefined,
    test_users: peerOptions.testUsers?.length === 1 ? peerOptions.testUsers[0] : 'all',
    language_tags: peerOptions.languageTags?.length ? peerOptions.languageTags.join(',') : undefined,
    limit: peerOptions.limit,
    order_by: peerOptions.orderBy
      ? peerOptions.orderBy
      : // if hasBackgroundTags option is set, we can't select an orderBy
        peerOptions.hasBackgroundTags !== undefined
        ? undefined
        : 'created_at',
    page: peerOptions.page,
    relationship_tags: peerOptions.relationshipTags?.length ? peerOptions.relationshipTags.join(',') : undefined,
    search: peerOptions.search,
    spirituality_tags: peerOptions.spiritualityTags?.length ? peerOptions.spiritualityTags.join(',') : undefined,
    tier_id: peerOptions.tierId?.length ? peerOptions.tierId.join(',') : undefined,
    user_type: peerOptions.userType?.length ? peerOptions.userType.join(',') : undefined,
    client_ids: peerOptions.clientIds?.length ? peerOptions.clientIds.join(',') : undefined,
    domain_id: peerOptions.domainId,
    content_created_after: peerOptions.contentCreatedAfter || undefined,
    content_created_before: peerOptions.contentCreatedBefore || undefined,
    graded_by_user_ids: peerOptions.gradedByUserIds?.length ? peerOptions.gradedByUserIds.join(',') : undefined,
    graded_after: peerOptions.gradedAfter || undefined,
    graded_before: peerOptions.gradedBefore || undefined,
    needs_any_content_review_types: contentReviewTypes.length ? contentReviewTypes.join(',') : undefined,
    tag_ids: peerOptions.tagIds?.length ? peerOptions.tagIds.join(',') : undefined,
  };

  Object.entries(mapOptionToParam).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params[key] = value;
    }
  });

  return params;
};

const getPeers = async (peerOptions: PeerOptions): Promise<ListenerRoleResponse> => {
  const params = buildQueryParams(peerOptions);

  return await axiosGet(
    '/listeners/',
    {
      ...params,
    },
    'v3',
  ).then((listenerRoleResponse) => listenerRoleResponse.data);
};

export const usePeers = (peerOptions: PeerOptions) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<ListenerRoleResponse>(
    ['peers', ...Object.values(peerOptions)],
    () => getPeers(peerOptions),
  );
  return { data, isLoading, error, refetch, isFetching };
};

const getListenerProfile = async (listenerId: number): Promise<ListenerRole> => {
  return await axiosGet(`/listeners/${listenerId}/profile`, {}, 'v3').then((listenerRole) => listenerRole.data);
};

export const useListenerProfile = (listenerId: number) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<ListenerRole>(['listenerProfile', listenerId], () =>
    getListenerProfile(listenerId),
  );

  const listenerProfile = data || undefined;

  return { data: listenerProfile, isLoading, error, refetch, isFetching };
};
